/**
 * API e stato server del dettaglio titolo (SPEC-016, F-067).
 *
 * ⚠️ Prezzi/importi sono STRINGHE decimali (ADR-0006): mai `parseFloat` per
 * calcoli, solo `lib/format.ts` per la visualizzazione.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AssetDomain } from '@/lib/asset-domain';
import { api, apiResourceUrl } from '@/lib/api-client';
import type { AnalysisSummary } from '@/features/watchlist/ai-analysis-api';

// -----------------------------------------------------------------------------
// Tipi (speculari ai DTO di instrument-detail.service.ts)
// -----------------------------------------------------------------------------

export type InstrumentIdentity = {
  id: string;
  name: string;
  /** Nome pubblico (F-087). Affianca `name`, non lo sostituisce. */
  commercialName: string | null;
  /**
   * ⚠️ Il provider chiama questo simbolo in un modo che non ha nulla a che
   * vedere con il nome a catalogo: il simbolo potrebbe puntare a un'altra
   * societa', e con lui prezzo, valuta e serie storica (bug `20260826-01`).
   */
  providerNameMismatch: boolean;
  isin: string | null;
  symbol: string | null;
  trading212Ticker: string | null;
  instrumentType: string;
  /** L'ambito (SPEC-026 §2): `crypto` per una moneta nativa, `stock` per tutto ciò che ha una borsa. */
  domain: AssetDomain;
  /** Solo per l'ambito crypto (F-147): rank per capitalizzazione e regola delle top 200. `null` per la borsa. */
  crypto: {
    marketCapRank: number | null;
    marketCap: string | null;
    marketCapCurrency: string | null;
    rankAsOf: string | null;
    investable: { ok: boolean; reason: string | null };
  } | null;
  assetClass: string | null;
  currency: string;
  exchange: string | null;
  figi: string | null;
  createdAt: string;
};

export type InstrumentMappingSection = {
  providerSymbol: string | null;
  quoteProvider: string | null;
  mappingStatus: string | null;
  mappingUpdatedAt: string | null;
  mappingCandidates: Array<{
    ticker: string;
    exchange: string | null;
    figi: string | null;
    currency: string | null;
    securityType: string | null;
    name?: string | null;
  }> | null;
  marketMic: string | null;
  marketResolutionStatus: string;
  marketResolvedAt: string | null;
};

export type MarketSection = {
  mic: string;
  name: string;
  city: string;
  currency: string;
  timezoneIana: string;
  trades24x7: boolean;
  status: string;
  nextOpen: string | null;
  nextClose: string | null;
  continuousOpen: string;
  continuousClose: string;
  briefingTimeLocal: string | null;
  triggerEnabled: boolean;
  latestBriefing: { id: string; briefingDate: string; status: string } | null;
};

export type QuoteSection = {
  price: string;
  currency: string;
  source: string;
  table: 'watchlist_price_snapshots' | 'price_history';
  capturedAt: string;
  kind: 'intraday' | 'eod';
  previousClose: string | null;
  changeVsPreviousClosePct: string | null;
  change5dPct: string | null;
  min5d: string | null;
  max5d: string | null;
};

export type Position = {
  portfolio: {
    id: string;
    name: string;
    portfolioType: string;
    baseCurrency: string;
    connectedProvider: string | null;
  };
  open: boolean;
  quantity: string | null;
  avgBuyPrice: string | null;
  invested: string | null;
  realizedPl: string | null;
  firstBuyDate: string | null;
  snapshotDate: string | null;
  currentPrice: string | null;
  marketValue: string | null;
  unrealizedPl: string | null;
  unrealizedPlPct: string | null;
  weightPct: string | null;
  currency: string;
};

export type InstrumentTransaction = {
  id: string;
  txType: string;
  txDate: string;
  quantity: string | null;
  price: string | null;
  amount: string;
  currency: string;
  portfolio: { id: string; name: string };
  origin: 'ai_analysis' | 'manual' | 'broker_sync' | 'import';
  sourceAnalysisId: string | null;
};

export type PositionsSection = {
  items: Position[];
  transactions: InstrumentTransaction[];
  transactionsTotal: number;
  incompleteValuation: boolean;
  totalMarketValue: string | null;
  totalUnrealizedPl: string | null;
};

export type WatchlistSection = {
  itemId: string;
  addedAt: string;
  addedPrice: string | null;
  addedPriceCurrency: string | null;
  targetPrice: string | null;
  notes: string | null;
  deltaPct: string | null;
};

export type WishlistEntry = {
  id: string;
  portfolio: { id: string; name: string; baseCurrency: string };
  maxExposureAmount: string | null;
  maxExposureCurrency: string;
  maxPortfolioPercentage: string | null;
  expiresAt: string;
  daysRemaining: number;
  expired: boolean;
  internalNotes: string | null;
  aiNotes: string | null;
};

export type AnalysesSection = {
  latest: AnalysisSummary | null;
  recent: AnalysisSummary[];
  quota: { used: number; limit: number; resetAt: string };
};

export type CoverageEntry = {
  source: string;
  table: 'price_history' | 'watchlist_price_snapshots';
  points: number;
  from: string | null;
  to: string | null;
};

export type ProviderChainEntry = {
  provider: string;
  role: 'primary' | 'fallback';
  enabled: boolean;
  window: 'day' | 'month' | null;
  limit: number | null;
  used: number | null;
};

export type NewsArticle = {
  headline: string;
  url: string;
  source: string | null;
  publishedAt: string | null;
  /** Sentiment del PROVIDER, -1..1: mai un giudizio nostro. Spesso `null`. */
  sentiment: number | null;
};

export type NewsSection = {
  /** `false` = raccolta spenta: un elenco vuoto non significa «nessuna notizia». */
  enabled: boolean;
  items: NewsArticle[];
};

export type InstrumentDetail = {
  identity: InstrumentIdentity;
  mapping: InstrumentMappingSection;
  market: MarketSection | null;
  quote: QuoteSection | null;
  positions: PositionsSection | null;
  watchlist: WatchlistSection | null;
  wishlist: WishlistEntry[] | null;
  analyses: AnalysesSection | null;
  news: NewsSection | null;
  /** Tag di QUESTO utente su questo titolo (F-079). Vuoto ≠ non disponibile. */
  tags: string[] | null;
  coverage: CoverageEntry[] | null;
  providers: ProviderChainEntry[] | null;
};

export type SeriesRange = '1d' | '5d' | '7d' | '1m' | '3m' | '6m' | 'ytd' | '1y' | '5y' | 'max';
export type SeriesGranularity = 'daily' | 'intraday';

export type SeriesPoint = {
  time: string;
  open: string | null;
  high: string | null;
  low: string | null;
  close: string | null;
  adjustedClose: string | null;
  volume: string | null;
  currency: string;
  source: string;
};

export type SeriesGap = { from: string; to: string; sessions: number };

export type InstrumentSeries = {
  instrumentId: string;
  granularity: SeriesGranularity;
  range: SeriesRange;
  from: string;
  to: string;
  points: SeriesPoint[];
  gaps: SeriesGap[];
  sources: Array<{ source: string; points: number; from: string; to: string }>;
};

export type BackfillResult = { taskId: string | null; deduplicated: boolean };

/** Esito dello scaricamento del dettaglio orario (F-072). */
export type IntradayRefreshResult = {
  /** `false` = dati già freschi, nessuna chiamata al provider. */
  refreshed: boolean;
  reason: 'fresh' | null;
  source: string;
  intervalMinutes: number;
  windowDays: number;
  fetched: number;
  inserted: number;
  lastBarAt: string | null;
};

// -----------------------------------------------------------------------------
// Hook
// -----------------------------------------------------------------------------

export const instrumentKeys = {
  detail: (id: string) => ['instruments', 'detail', id] as const,
  series: (id: string, range: SeriesRange, granularity: SeriesGranularity) =>
    ['instruments', 'series', id, range, granularity] as const,
};

export function useInstrumentDetail(instrumentId: string) {
  return useQuery({
    queryKey: instrumentKeys.detail(instrumentId),
    queryFn: ({ signal }) => api.get<InstrumentDetail>(`/instruments/${instrumentId}`, signal),
  });
}

export function useInstrumentSeries(
  instrumentId: string,
  range: SeriesRange,
  granularity: SeriesGranularity,
) {
  return useQuery({
    queryKey: instrumentKeys.series(instrumentId, range, granularity),
    queryFn: ({ signal }) =>
      api.get<InstrumentSeries>(
        `/instruments/${instrumentId}/quotes?range=${range}&granularity=${granularity}`,
        signal,
      ),
  });
}

/**
 * Scarica il dettaglio orario degli ultimi giorni (F-072).
 *
 * È una mutazione e non un parametro della query della serie perché **spende**:
 * consuma quota e chiama un provider esterno. La serie resta di sola lettura,
 * e cambiare orizzonte sul grafico continua a non costare nulla — è la regola
 * di SPEC-016 §3.2, e questa è l'unica porta che la attraversa dichiarandolo.
 */
export function useRefreshIntraday(instrumentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<IntradayRefreshResult>(`/instruments/${instrumentId}/quotes/intraday`, {}),
    onSuccess: (result) => {
      // Niente da rileggere se non è partita alcuna chiamata: invalidare
      // comunque farebbe ripartire la query per riottenere gli stessi punti.
      if (!result.refreshed) return;
      void queryClient.invalidateQueries({ queryKey: ['instruments', 'series', instrumentId] });
      void queryClient.invalidateQueries({ queryKey: instrumentKeys.detail(instrumentId) });
    },
  });
}

export function useRequestBackfill(instrumentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { from: string; to: string }) =>
      api.post<BackfillResult>(`/instruments/${instrumentId}/quotes/backfill`, input),
    onSuccess: () => {
      // Il task è asincrono: i dati non sono ancora arrivati, ma la copertura
      // può cambiare al prossimo refetch — si invalida senza aspettare.
      void queryClient.invalidateQueries({ queryKey: ['instruments', 'series', instrumentId] });
      void queryClient.invalidateQueries({ queryKey: instrumentKeys.detail(instrumentId) });
    },
  });
}

/**
 * Scarica il CSV dello storico. Non un semplice `<a href>`: l'endpoint è
 * autenticato via Bearer, quindi serve il client HTTP (che gestisce anche il
 * refresh del token) e poi un blob locale.
 */
export async function downloadHistoryCsv(instrumentId: string, filename: string): Promise<void> {
  const csv = await api.getText(`/instruments/${instrumentId}/quotes/export.csv`);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

/** Logo del titolo (F-186): rotta pubblica, la chiede direttamente un `<img>`. */
export function instrumentLogoUrl(instrumentId: string): string {
  return apiResourceUrl(`/instruments/${instrumentId}/logo`);
}
